import { useEffect, useRef, useState } from 'react'
import { IconeChevronDireita, IconeChevronEsquerda } from '../icones'
import { MODULOS, type IdModulo } from './modulos'
import type { ModoLayout } from './useModoLayout'

interface Props {
  moduloAtual: IdModulo
  aoTrocar: (id: IdModulo) => void
  modoLayout: ModoLayout
}

interface EstadoRolagemNav {
  inicio: boolean
  fim: boolean
}

/** Folga para não acender a seta por causa de arredondamento de subpixel. */
const FOLGA_ROLAGEM = 4

/**
 * Quais lados da navegação ainda têm módulo escondido. Mesma conta de
 * `estadoDeRolagem` em `BarraFerramentasCenario.tsx` — duplicada aqui, não
 * importada de lá, porque workspace e ribbon são áreas independentes e a
 * função é pequena o bastante para não justificar o acoplamento.
 */
function estadoDeRolagem(scrollLeft: number, scrollWidth: number, clientWidth: number): EstadoRolagemNav {
  const sobra = scrollWidth - clientWidth
  if (sobra <= FOLGA_ROLAGEM) return { inicio: false, fim: false }
  return {
    inicio: scrollLeft > FOLGA_ROLAGEM,
    fim: scrollLeft < sobra - FOLGA_ROLAGEM,
  }
}

/**
 * Navegação entre os seis módulos. É a mesma em toda a aplicação — não existe
 * uma barra por módulo, e o usuário não reaprende onde clicar ao trocar de
 * assunto.
 *
 * No desktop e no tablet mostra ícone + texto. Em larguras de notebook mais
 * estreitas os seis rótulos por extenso podem não caber: a faixa rola, e as
 * setas aparecem só quando há módulo escondido de cada lado — sem elas a
 * barra de rolagem ficava escondida (de propósito, para não quebrar a altura
 * fixa) e sumir um módulo de vista parecia bug, não "role para o lado".
 *
 * No mobile o texto sai e sobra o ícone, com o nome do módulo em `aria-label`
 * e em `title`: nessa largura os módulos viram colunas de mesma largura (ver
 * `.menu-horizontal--compacto`), todos visíveis, sem rolar.
 */
export function NavegacaoModulos({ moduloAtual, aoTrocar, modoLayout }: Props) {
  const somenteIcone = modoLayout === 'mobile'
  const navRef = useRef<HTMLElement>(null)
  const [rolagem, setRolagem] = useState<EstadoRolagemNav>({ inicio: false, fim: false })

  useEffect(() => {
    const nav = navRef.current
    if (!nav || somenteIcone) {
      setRolagem({ inicio: false, fim: false })
      return
    }

    const medir = () => {
      setRolagem((atual) => {
        const novo = estadoDeRolagem(nav.scrollLeft, nav.scrollWidth, nav.clientWidth)
        return novo.inicio === atual.inicio && novo.fim === atual.fim ? atual : novo
      })
    }

    medir()
    nav.addEventListener('scroll', medir, { passive: true })
    let observador: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observador = new ResizeObserver(medir)
      observador.observe(nav)
    }
    return () => {
      nav.removeEventListener('scroll', medir)
      observador?.disconnect()
    }
  }, [somenteIcone])

  const rolar = (direcao: -1 | 1) => {
    const nav = navRef.current
    if (!nav) return
    nav.scrollBy({ left: direcao * nav.clientWidth * 0.8, behavior: 'smooth' })
  }

  return (
    <div className="menu-horizontal-container">
      <nav
        ref={navRef}
        className={`menu-horizontal${somenteIcone ? ' menu-horizontal--compacto' : ''}`}
        aria-label="Módulos do laboratório"
      >
        {MODULOS.map((modulo) => {
          const ativo = modulo.id === moduloAtual
          return (
            <button
              key={modulo.id}
              type="button"
              className={ativo ? 'item-menu-horizontal ativo' : 'item-menu-horizontal'}
              onClick={() => aoTrocar(modulo.id)}
              aria-current={ativo ? 'page' : undefined}
              aria-label={somenteIcone ? modulo.rotulo : undefined}
              title={modulo.titulo}
            >
              <modulo.Icone tamanho={18} />
              {!somenteIcone && <span>{modulo.rotulo}</span>}
            </button>
          )
        })}
      </nav>

      {rolagem.inicio && (
        <button
          type="button"
          className="menu-horizontal__seta menu-horizontal__seta--inicio"
          onClick={() => rolar(-1)}
          aria-hidden="true"
          tabIndex={-1}
          title="Ver módulos anteriores"
        >
          <IconeChevronEsquerda tamanho={16} />
        </button>
      )}
      {rolagem.fim && (
        <button
          type="button"
          className="menu-horizontal__seta menu-horizontal__seta--fim"
          onClick={() => rolar(1)}
          aria-hidden="true"
          tabIndex={-1}
          title="Ver mais módulos"
        >
          <IconeChevronDireita tamanho={16} />
        </button>
      )}
    </div>
  )
}
